<template>
  <v-dialog persistent v-model="dialog" max-width="500px">
    <v-card>
      <v-card-title>
        <span class="headline">{{ $t("Attach a licence") }}</span>
      </v-card-title>
      <v-card-subtitle>{{ $t("* Required fields") }}</v-card-subtitle>
      <v-card-text>
        <validation-observer ref="form">
          <v-container>
            <validation-provider
              v-slot="{ errors }"
              name="device_id"
              rules="required"
            >
              <v-autocomplete
                data-cy="autocomplete-device-licence"
                :label="`${$t('Device')} *`"
                :items="devices"
                item-text="serial_number"
                return-object
                v-model="device"
                :error-messages="errors"
              >
                <template v-slot:append-outer>
                  <v-slide-x-reverse-transition mode="out-in">
                    <v-icon
                      :color="'primary'"
                      @click="openDialogDevice"
                      v-text="'mdi-plus'"
                    ></v-icon>
                  </v-slide-x-reverse-transition> </template
              ></v-autocomplete>
            </validation-provider>
            <validation-provider v-slot="{ errors }" name="client_id">
              <v-autocomplete
                data-cy="autocomplete-client-licence"
                :label="$t('Customer')"
                :items="clientsFilteredByEntity"
                item-text="name"
                item-value="id"
                v-model="clientId"
                :error-messages="errors"
              >
                <template v-slot:append-outer>
                  <v-slide-x-reverse-transition mode="out-in">
                    <v-icon
                      :color="'primary'"
                      @click="openDialogClient"
                      v-text="'mdi-plus'"
                    ></v-icon>
                  </v-slide-x-reverse-transition>
                </template>
              </v-autocomplete>
            </validation-provider>
          </v-container>
        </validation-observer>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" text @click.stop="dialog = false">{{
          $t("Cancel")
        }}</v-btn>
        <v-btn
          color="primary"
          data-cy="button-save-attach-licence"
          :loading="loading"
          text
          @click.stop="save"
          >{{ $t("Save") }}</v-btn
        >
      </v-card-actions>
      <add-edit-device-dialog
        v-model="dialogDevice"
        @created="$store.commit('licences/setDevice', $event)"
        @close="closeDialogDevice"
      ></add-edit-device-dialog>
      <add-edit-client-dialog
        v-model="dialogClient"
        @created="$store.commit('devices/setClientId', $event.id)"
        @close="closeDialogClient"
      ></add-edit-client-dialog>
    </v-card>
  </v-dialog>
</template>

<script>
import snackbar from "@/mixins/snackbar";
import { mapGetters, mapState } from "vuex";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import AddEditClientDialog from "./AddEditClientDialog";
import AddEditDeviceDialog from "./AddEditDeviceDialog";

export default {
  name: "AttachLicenceDialog",
  props: ["value"],
  components: {
    ValidationObserver,
    ValidationProvider,
    AddEditClientDialog,
    AddEditDeviceDialog,
  },
  mixins: [snackbar],
  data() {
    return {
      loading: false,
      dialogDevice: false,
      dialogClient: false,
    };
  },
  watch: {
    dialog(value) {
      if (value && this.$refs.form) {
        this.$refs.form.reset();
      } else if (!value) {
        this.$store.commit("licences/setSelectedItem", null);
        this.$store.commit("licences/setDevice", null);
        this.$store.commit("devices/setClientId", null);
      }
    },
  },
  computed: {
    ...mapState({
      editedId: (state) => state.clients.editedId,
      entities: (state) => state.auth.user.reseller_entities,
      clients: (state) => state.clients.items,
      licence: (state) => state.licences.selectedItem,
    }),
    editedItem: {
      get() {
        return this.$store.state.clients.editedItem;
      },
      set(value) {
        this.$store.commit("clients/setEditedItem", value);
      },
    },
    clientsFilteredByEntity() {
      if (!this.editedItem.reseller_entity_id) {
        return this.clients;
      }

      return this.clients.filter(
        (client) =>
          client.reseller_entity_id === this.editedItem.reseller_entity_id
      );
    },

    dialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    ...mapGetters("devices", {
      devices: "filterByResellerEntityAndApplication",
    }),
    device: {
      get() {
        return this.$store.state.licences.device;
      },
      set(value) {
        this.$store.commit("licences/setDevice", value);
        this.$store.commit("devices/setClientId", value.client?.id ?? null);
      },
    },
    clientId: {
      get() {
        return this.$store.state.devices.clientId;
      },
      set(value) {
        this.$store.commit("devices/setClientId", value);
      },
    },
  },
  methods: {
    save() {
      this.$refs.form.validate().then((success) => {
        if (!success) {
          return;
        }
        this.loading = true;

        let promises = [];

        if (this.clientId !== null) {
          promises.push(
            this.$store.dispatch("devices/updateClientId", {
              deviceId: this.device.id,
              clientId: this.clientId,
            })
          );
        }

        promises.push(this.$store.dispatch("licences/attachDevice"));

        Promise.all(promises)
          .then(() => {
            this.$refs.form.reset();
            this.loading = false;
            this.dialog = false;
            this.snackbar(
              "success",
              this.$t("snackbar-message-licence.attached")
            );
          })
          .catch(this.responseError);
      });
    },
    openDialogDevice() {
      this.$store.commit("devices/setEditedItem", {
        ...this.$store.state.devices.editedItem,
        reseller_entity_id: this.licence.reseller_entity_id,
      });
      this.dialogDevice = true;
    },
    openDialogClient() {
      this.$store.commit("clients/setEditedItem", {
        ...this.$store.state.clients.editedItem,
        reseller_entity_id: this.licence.reseller_entity_id,
      });
      this.dialogClient = true;
    },
    closeDialogDevice() {
      this.dialogDevice = false;
      setTimeout(() => {
        this.$store.dispatch("devices/resetEditedItem");
      }, 300);
    },
    closeDialogClient() {
      this.dialogClient = false;
      setTimeout(() => {
        this.$store.dispatch("clients/resetEditedItem");
      }, 300);
    },
    responseError(error) {
      this.loading = false;
      if (error.response && error.response.status === 422) {
        let errors = error.response.data.errors;
        this.$refs.form.setErrors(errors);
      } else {
        this.snackbar("error", this.$t("snackbar-message-licence.error"));
      }
    },
  },
};
</script>